import { useState, useEffect, useCallback } from 'react';
// Edition-aware marketplace queries (use these for marketplace display)
import {
  getMarketplacePieces,
  getMarketplacePiecesByCategory,
  searchMarketplacePieces,
  getMarketplacePiecesByCreator,
  getMasterPieceById
} from '../utils/marketplaceQueries.js';

export const usePieces = (category = null, page = 0, limit = 20) => {
  const [pieces, setPieces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 0,
    limit: 20,
    total: 0,
    totalPages: 0
  });

  const fetchPieces = useCallback(async () => {
    try {
      console.log('🔍 usePieces: Starting to fetch pieces, category:', category, 'page:', page, 'limit:', limit);
      setLoading(true);
      setError(null);

      let result;
      if (category) {
        console.log('🔍 usePieces: Fetching pieces by category:', category);
        // Use edition-aware query for marketplace display
        result = await getMarketplacePiecesByCategory(category, page, limit);
      } else {
        console.log('🔍 usePieces: Fetching all pieces');
        // Use edition-aware query for marketplace display (shows only masters with aggregated data)
        result = await getMarketplacePieces(page, limit);
      }

      console.log('🔍 usePieces: Pieces count:', result.pieces?.length || 0);
      
      setPieces(result.pieces);
      setPagination({
        page: result.page,
        limit: result.limit,
        total: result.total,
        totalPages: result.totalPages
      });
    } catch (err) {
      console.error('❌ usePieces: Error fetching pieces:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [category, page, limit]);

  useEffect(() => {
    fetchPieces();
  }, [fetchPieces]);

  return {
    pieces,
    loading,
    error,
    pagination,
    refetch: fetchPieces
  };
};

export const usePiece = (id) => {
  const [piece, setPiece] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPiece = useCallback(async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError(null);
      // Use edition-aware query to get master piece with all edition data
      const result = await getMasterPieceById(id);
      setPiece(result);
    } catch (err) {
      console.error('Error fetching piece:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchPiece();
  }, [fetchPiece]);

  return {
    piece,
    loading,
    error,
    refetch: fetchPiece
  };
};

export const useSearchPieces = (query, page = 0, limit = 20) => {
  const [pieces, setPieces] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 0,
    limit: 20,
    total: 0,
    totalPages: 0
  });

  const search = useCallback(async () => {
    if (!query.trim()) {
      setPieces([]);
      setPagination({
        page: 0,
        limit: 20,
        total: 0,
        totalPages: 0
      });
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Use edition-aware search for marketplace display
      const result = await searchMarketplacePieces(query, page, limit);

      setPieces(result.pieces);
      setPagination({
        page: result.page,
        limit: result.limit,
        total: result.total,
        totalPages: result.totalPages
      });
    } catch (err) {
      console.error('Error searching pieces:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [query, page, limit]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      search();
    }, 300); // Debounce search

    return () => clearTimeout(timeoutId);
  }, [search]);

  return {
    pieces,
    loading,
    error,
    pagination,
    search
  };
};

export const useCreatorPieces = (walletAddress, page = 0, limit = 20) => {
  const [pieces, setPieces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 0,
    limit: 20,
    total: 0,
    totalPages: 0
  });

  const fetchCreatorPieces = useCallback(async () => {
    if (!walletAddress) return;

    try {
      setLoading(true);
      setError(null);

      // Use edition-aware query for marketplace display (shows only masters)
      const result = await getMarketplacePiecesByCreator(walletAddress, page, limit);

      setPieces(result.pieces);
      setPagination({
        page: result.page,
        limit: result.limit,
        total: result.total,
        totalPages: result.totalPages
      });
    } catch (err) {
      console.error('Error fetching creator pieces:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [walletAddress, page, limit]);

  useEffect(() => {
    fetchCreatorPieces();
  }, [fetchCreatorPieces]);

  return {
    pieces,
    loading,
    error,
    pagination,
    refetch: fetchCreatorPieces
  };
};
